import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { databaseService } from '../services/database';
import { useAuth } from './AuthContext';
import dayjs from 'dayjs';
import { numbersEqual, generateId, getCurrentDate } from '../utils/helpers';
import { refreshPricesAndCreateSnapshots } from '../services/priceRefreshService';
import { refreshExchangeRatesIfNeeded } from '../services/exchangeRateRefreshService';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [goals, setGoals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [fundPrices, setFundPrices] = useState([]); // Centralized fund/security prices
  const [exchangeRates, setExchangeRates] = useState({
    baseCurrency: 'GBP',
    lastUpdated: new Date().toISOString().split('T')[0],
    rates: {
      GBP: 1.00,
      USD: 1.27,
    }
  });
  const [properties, setProperties] = useState([]);
  const [mortgages, setMortgages] = useState([]);
  const [proposedTransactions, setProposedTransactions] = useState([]);
  const [schoolFeePlans, setSchoolFeePlans] = useState([]);
  const [settings, setSettings] = useState({ displayCurrency: 'GBP', theme: 'light' });
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Load data from database when user changes
  useEffect(() => {
    console.log('DataContext effect running, currentUser:', currentUser?.uid);

    // Reset loading state when user changes
    setLoading(true);
    setInitialLoadComplete(false);

    // If no user, clear all data and set loading to false
    if (!currentUser) {
      console.log('No user, clearing all data');
      databaseService.setUserId(null);
      setPortfolios([]);
      setGoals([]);
      setTransactions([]);
      setSnapshots([]);
      setFundPrices([]);
      setProperties([]);
      setMortgages([]);
      setProposedTransactions([]);
      setSchoolFeePlans([]);
      setExchangeRates({
        baseCurrency: 'GBP',
        lastUpdated: new Date().toISOString().split('T')[0],
        rates: {
          GBP: 1.00,
          USD: 1.27,
        }
      });
      setSettings({ displayCurrency: 'GBP', theme: 'light' });
      setLoading(false);
      return;
    }

    console.log('User is authenticated, setting user ID and loading data from Firebase');

    // Set user ID BEFORE loading data
    databaseService.setUserId(currentUser.uid);

    // Load data from Firebase
    const loadData = async () => {
      try {
        const [
          savedPortfolios,
          savedGoals,
          savedTransactions,
          savedSnapshots,
          savedFundPrices,
          savedProperties,
          savedMortgages,
          savedProposedTransactions,
          savedSchoolFeePlans,
          savedExchangeRates,
          savedSettings
        ] = await Promise.all([
          databaseService.loadData('portfolios'),
          databaseService.loadData('goals'),
          databaseService.loadData('transactions'),
          databaseService.loadData('snapshots'),
          databaseService.loadData('fundPrices'),
          databaseService.loadData('properties'),
          databaseService.loadData('mortgages'),
          databaseService.loadData('proposedTransactions'),
          databaseService.loadData('schoolFeePlans'),
          databaseService.loadData('exchangeRates'),
          databaseService.loadData('settings'),
        ]);

        console.log('Loaded data from storage:', {
          portfolios: savedPortfolios,
          goals: savedGoals,
          transactions: savedTransactions,
          snapshots: savedSnapshots,
          fundPrices: savedFundPrices,
          exchangeRates: savedExchangeRates,
          settings: savedSettings,
        });

        // Always set state to loaded data or empty defaults
        setPortfolios(savedPortfolios || []);
        setGoals(savedGoals || []);
        setTransactions(savedTransactions || []);
        setSnapshots(savedSnapshots || []);
        setFundPrices(savedFundPrices || []);
        setProperties(savedProperties || []);
        setMortgages(savedMortgages || []);
        setProposedTransactions(savedProposedTransactions || []);
        setSchoolFeePlans(savedSchoolFeePlans || []);
        setExchangeRates(savedExchangeRates || {
          baseCurrency: 'GBP',
          lastUpdated: new Date().toISOString().split('T')[0],
          rates: {
            GBP: 1.00,
            USD: 1.27,
            EUR: 1.17,
            JPY: 188.50,
          }
        });
        setSettings(savedSettings || { displayCurrency: 'GBP', theme: 'light' });

        setLoading(false);
        setInitialLoadComplete(true);
      } catch (error) {
        console.error('Error loading data:', error);
        // Don't show error toast if it's just a 404 (no data yet for new user)
        if (!error.message.includes('Not Found')) {
          toast.error('Error loading data from storage');
        }
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };

    loadData();
  }, [currentUser]);

  // Auto-refresh fund prices on app load
  const hasRunPriceRefresh = useRef(false);

  useEffect(() => {
    // Only run once after initial load is complete
    if (!loading && initialLoadComplete && !hasRunPriceRefresh.current && currentUser) {
      hasRunPriceRefresh.current = true;

      const autoRefreshPrices = async () => {
        try {
          const results = await refreshPricesAndCreateSnapshots({
            fundPrices,
            portfolios,
            snapshots,
            updateFundPrice,
            addSnapshot,
            silent: true, // Don't show toasts on auto-refresh
            skipSnapshots: true, // Don't create snapshots here - we'll do it after portfolio values update
          });

          // Log results for debugging
          if (results.pricesRefreshed) {
            console.log('Auto-refreshed fund prices:', results.priceResults);
          }
        } catch (error) {
          console.error('Error in auto-refresh:', error);
        }
      };

      autoRefreshPrices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, initialLoadComplete, currentUser]);

  // Auto-refresh exchange rates on app load
  const hasRunExchangeRateRefresh = useRef(false);

  useEffect(() => {
    // Only run once after initial load is complete
    if (!loading && initialLoadComplete && !hasRunExchangeRateRefresh.current && currentUser) {
      hasRunExchangeRateRefresh.current = true;

      const autoRefreshExchangeRates = async () => {
        try {
          const results = await refreshExchangeRatesIfNeeded({
            exchangeRates,
            updateExchangeRates,
            silent: true, // Don't show toasts on auto-refresh
          });

          // Log results for debugging
          if (results.refreshed) {
            console.log('Auto-refreshed exchange rates:', results.ratesCount, 'currencies updated');
          } else if (results.error && !results.skipped) {
            console.warn('Failed to auto-refresh exchange rates:', results.error);
          }
        } catch (error) {
          console.error('Error in exchange rate auto-refresh:', error);
        }
      };

      autoRefreshExchangeRates();
    }
  }, [loading, initialLoadComplete, currentUser, exchangeRates]);

  // Auto-create daily snapshots after portfolio values have been updated
  const hasCreatedTodaysSnapshots = useRef(false);
  const lastSnapshotCheck = useRef(null);

  useEffect(() => {
    // Wait for initial load and price refresh to complete
    if (!loading && initialLoadComplete && hasRunPriceRefresh.current && currentUser && portfolios.length > 0) {
      const today = getCurrentDate();

      // Reset the flag if it's a new day
      if (lastSnapshotCheck.current !== today) {
        hasCreatedTodaysSnapshots.current = false;
        lastSnapshotCheck.current = today;
      }

      // Only create snapshots once per day
      if (!hasCreatedTodaysSnapshots.current) {
        const shouldCreateSnapshots = portfolios.some(portfolio => {
          const hasSnapshotToday = snapshots.some(snap =>
            snap.portfolioId === portfolio.id && snap.date === today
          );
          return !hasSnapshotToday;
        });

        if (shouldCreateSnapshots) {
          // Set flag BEFORE creating to prevent race condition
          hasCreatedTodaysSnapshots.current = true;

          const createSnapshots = () => {
            try {
              // Batch create all snapshots in a single state update
              const newSnapshots = [];

              for (const portfolio of portfolios) {
                const hasSnapshotToday = snapshots.some(snap =>
                  snap.portfolioId === portfolio.id && snap.date === today
                );

                if (!hasSnapshotToday) {
                  newSnapshots.push({
                    id: generateId('snap'),
                    portfolioId: portfolio.id,
                    date: today,
                    value: portfolio.currentValue || 0,
                    costBasis: portfolio.costBasis || 0,
                  });
                }
              }

              // Single batch update to prevent multiple renders
              if (newSnapshots.length > 0) {
                setSnapshots(prev => [...prev, ...newSnapshots]);
                console.log(`Created ${newSnapshots.length} daily snapshot(s) with updated values`);
              }
            } catch (error) {
              console.error('Error creating daily snapshots:', error);
              // Reset flag on error to allow retry
              hasCreatedTodaysSnapshots.current = false;
            }
          };

          createSnapshots();
        }
      }
    }
  }, [loading, initialLoadComplete, currentUser, portfolios, snapshots]);

  // FIX: Batch all data saves together to avoid race conditions
  // Use a ref to debounce saves and prevent too many rapid saves
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (!loading && currentUser && initialLoadComplete) {
      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce saves by 500ms to batch rapid changes
      saveTimeoutRef.current = setTimeout(() => {
        const saveAllData = async () => {
          try {
            // Save all data collections in parallel
            await Promise.all([
              databaseService.saveData('portfolios', portfolios),
              databaseService.saveData('goals', goals),
              databaseService.saveData('transactions', transactions),
              databaseService.saveData('snapshots', snapshots),
              databaseService.saveData('fundPrices', fundPrices),
              databaseService.saveData('properties', properties),
              databaseService.saveData('mortgages', mortgages),
              databaseService.saveData('proposedTransactions', proposedTransactions),
              databaseService.saveData('schoolFeePlans', schoolFeePlans),
              databaseService.saveData('exchangeRates', exchangeRates),
              databaseService.saveData('settings', settings),
            ]);
          } catch (error) {
            console.error('Error saving data:', error);
            toast.error('Failed to save data. Please try again.');
          }
        };

        saveAllData();
      }, 500);
    }

    // Cleanup timeout on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [portfolios, goals, transactions, snapshots, fundPrices, properties, mortgages, proposedTransactions, schoolFeePlans, exchangeRates, settings, loading, currentUser, initialLoadComplete]);


  // Portfolio operations
  const addPortfolio = (portfolio) => {
    const newPortfolio = {
      ...portfolio,
      id: generateId('portfolio'),
      createdAt: getCurrentDate(),
      updatedAt: getCurrentDate(),
    };
    setPortfolios(prev => [...prev, newPortfolio]);
    return newPortfolio;
  };

  const updatePortfolio = (portfolioId, updates) => {
    setPortfolios(prev =>
      prev.map(p =>
        p.id === portfolioId
          ? { ...p, ...updates, updatedAt: dayjs().format('YYYY-MM-DD') }
          : p
      )
    );
  };

  const deletePortfolio = (portfolioId) => {
    setPortfolios(prev => prev.filter(p => p.id !== portfolioId));
    // Clean up associated data
    setTransactions(prev => prev.filter(t => t.portfolioId !== portfolioId));
    setSnapshots(prev => prev.filter(s => s.portfolioId !== portfolioId));
    // Update goals to remove allocations for this portfolio
    setGoals(prev =>
      prev.map(goal => ({
        ...goal,
        allocations: goal.allocations?.filter(a => a.portfolioId !== portfolioId) || [],
      }))
    );
  };

  const updatePortfolioValue = (portfolioId, newValue, newCostBasis = null) => {
    // FIX: Add validation
    if (newValue < 0) {
      toast.error('Portfolio value cannot be negative');
      return false;
    }
    if (newCostBasis !== null && newCostBasis < 0) {
      toast.error('Cost basis cannot be negative');
      return false;
    }

    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (!portfolio) {
      toast.error('Portfolio not found');
      return false;
    }

    // Calculate the change in value
    const valueChange = newValue - portfolio.currentValue;
    const costBasisChange = newCostBasis !== null ? newCostBasis - portfolio.costBasis : 0;

    // Create a manual transaction to record this update
    const manualTransaction = {
      id: generateId('txn'),
      portfolioId: portfolioId,
      type: 'manual',
      amount: valueChange, // The change in value (positive or negative)
      date: getCurrentDate(),
      description: `Manual value adjustment: ${portfolio.currentValue.toFixed(2)} → ${newValue.toFixed(2)}${costBasisChange !== 0 ? `, Cost basis: ${portfolio.costBasis.toFixed(2)} → ${newCostBasis.toFixed(2)}` : ''}`,
      createdAt: getCurrentDate(),
    };

    setTransactions(prev => [...prev, manualTransaction]);

    // Create a snapshot
    const snapshot = {
      id: generateId('snap'),
      portfolioId: portfolioId,
      date: getCurrentDate(),
      value: newValue,
      costBasis: newCostBasis !== null ? newCostBasis : portfolio.costBasis,
    };

    setSnapshots(prev => [...prev, snapshot]);

    setPortfolios(prev =>
      prev.map(p =>
        p.id === portfolioId
          ? {
              ...p,
              currentValue: newValue,
              costBasis: newCostBasis !== null ? newCostBasis : p.costBasis,
              updatedAt: getCurrentDate(),
            }
          : p
      )
    );

    return true;
  };

  // Add snapshot
  const addSnapshot = (snapshotData) => {
    const snapshot = {
      id: generateId('snap'),
      portfolioId: snapshotData.portfolioId,
      date: snapshotData.date,
      value: snapshotData.value,
      costBasis: snapshotData.costBasis,
    };

    setSnapshots(prev => [...prev, snapshot]);
    return snapshot;
  };

  // Goal operations
  const addGoal = (goal) => {
    const newGoal = {
      ...goal,
      id: generateId('goal'),
      createdAt: getCurrentDate(),
    };
    setGoals(prev => [...prev, newGoal]);
    return newGoal;
  };

  const updateGoal = (goalId, updates) => {
    setGoals(prev =>
      prev.map(goal =>
        goal.id === goalId ? { ...goal, ...updates } : goal
      )
    );
  };

  const reorderGoals = (orderedIds) => {
    setGoals(prev => {
      const goalMap = Object.fromEntries(prev.map(g => [g.id, g]));
      return orderedIds.map(id => goalMap[id]).filter(Boolean);
    });
  };

  const deleteGoal = (goalId) => {
    setGoals(prev => prev.filter(goal => goal.id !== goalId));
    // Also remove associated transactions
    setTransactions(prev =>
      prev.map(txn =>
        txn.goalId === goalId ? { ...txn, goalId: null } : txn
      )
    );
  };

  // Transaction operations
  const addTransaction = (transaction) => {
    const newTransaction = {
      ...transaction,
      id: generateId('txn'),
      createdAt: getCurrentDate(),
    };
    setTransactions(prev => [...prev, newTransaction]);

    return newTransaction;
  };

  const updateTransaction = (transactionId, updates) => {
    const oldTransaction = transactions.find(t => t.id === transactionId);
    if (!oldTransaction) return;

    setTransactions(prev =>
      prev.map(txn =>
        txn.id === transactionId ? { ...txn, ...updates } : txn
      )
    );
    // Portfolio totals will be updated automatically by useEffect
  };

  const deleteTransaction = (transactionId) => {
    const transaction = transactions.find(t => t.id === transactionId);
    if (!transaction) return;

    setTransactions(prev => prev.filter(txn => txn.id !== transactionId));
    // Portfolio totals will be recalculated automatically by useEffect
    // (which derives values from pot transactions)
  };

  const importTransactions = (newTransactions) => {
    setTransactions(prev => {
      // Merge new transactions, avoiding duplicates by ID
      const existingIds = new Set(prev.map(t => t.id));
      const uniqueNewTransactions = newTransactions.filter(t => !existingIds.has(t.id));
      return [...prev, ...uniqueNewTransactions];
    });
    // Portfolio totals will be recalculated automatically by useEffect
    // (which derives values from pot transactions)
  };


  // Fund Price operations
  const addFundPrice = (fundPrice) => {
    const newFundPrice = {
      ...fundPrice,
      id: fundPrice.id || generateId('fund'),
      lastUpdated: getCurrentDate(),
    };
    setFundPrices(prev => [...prev, newFundPrice]);
    toast.success(`Fund price for ${fundPrice.name} added`);
    return newFundPrice;
  };

  const updateFundPrice = (fundId, updates, silent = false) => {
    setFundPrices(prev =>
      prev.map(fund => {
        if (fund.id === fundId) {
          // Store price history if price is changing
          let priceHistory = fund.priceHistory || [];
          let newPreviousPrice = fund.previousPrice;

          if (updates.price && updates.price !== fund.price) {
            // Price is actually changing, so record history and update previousPrice
            const historyEntry = {
              date: getCurrentDate(),
              price: fund.price, // Store old price before updating
              currency: fund.currency,
            };
            // Keep last 100 price points
            priceHistory = [historyEntry, ...priceHistory].slice(0, 100);
            // Only update previousPrice when price actually changes
            newPreviousPrice = fund.price;
          }

          return {
            ...fund,
            ...updates,
            priceHistory,
            lastUpdated: getCurrentDate(),
            previousPrice: newPreviousPrice,
          };
        }
        return fund;
      })
    );
    if (!silent) {
      toast.success('Fund price updated');
    }
  };

  const deleteFundPrice = (fundId) => {
    setFundPrices(prev => prev.filter(f => f.id !== fundId));
    toast.success('Fund price deleted');
    return true;
  };


  // Exchange rate operations
  const updateExchangeRates = (newRates) => {
    setExchangeRates(newRates);
    // Note: Exchange rates are saved to Firebase in the auto-save effect
    console.log('Exchange rates updated:', newRates.baseCurrency, Object.keys(newRates.rates).length, 'currencies');
  };

  // Settings operations
  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    toast.success('Settings updated successfully');
  };

  // Property operations
  const addProperty = (property) => {
    const newProperty = {
      ...property,
      id: generateId('property'),
      createdAt: getCurrentDate(),
      updatedAt: getCurrentDate(),
    };
    setProperties(prev => [...prev, newProperty]);
    // Sync bidirectional link: update the linked mortgage to point back
    if (newProperty.linkedMortgageId) {
      setMortgages(prev => prev.map(m =>
        m.id === newProperty.linkedMortgageId ? { ...m, linkedPropertyId: newProperty.id } : m
      ));
    }
    return newProperty;
  };

  const updateProperty = (propertyId, updates) => {
    setProperties(prev => {
      const oldProperty = prev.find(p => p.id === propertyId);
      // If linked mortgage changed, update old and new mortgage links
      if (oldProperty && oldProperty.linkedMortgageId !== updates.linkedMortgageId) {
        // Unlink old mortgage
        if (oldProperty.linkedMortgageId) {
          setMortgages(prevM => prevM.map(m =>
            m.id === oldProperty.linkedMortgageId ? { ...m, linkedPropertyId: null } : m
          ));
        }
        // Link new mortgage
        if (updates.linkedMortgageId) {
          setMortgages(prevM => prevM.map(m =>
            m.id === updates.linkedMortgageId ? { ...m, linkedPropertyId: propertyId } : m
          ));
        }
      }
      return prev.map(p =>
        p.id === propertyId ? { ...p, ...updates, updatedAt: getCurrentDate() } : p
      );
    });
  };

  const deleteProperty = (propertyId) => {
    setProperties(prev => prev.filter(p => p.id !== propertyId));
    // Unlink any mortgages that reference this property
    setMortgages(prev => prev.map(m =>
      m.linkedPropertyId === propertyId ? { ...m, linkedPropertyId: null } : m
    ));
  };

  // Mortgage operations
  const addMortgage = (mortgage) => {
    const newMortgage = {
      ...mortgage,
      id: generateId('mortgage'),
      createdAt: getCurrentDate(),
      updatedAt: getCurrentDate(),
    };
    setMortgages(prev => [...prev, newMortgage]);
    // Sync bidirectional link: update the linked property to point back
    if (newMortgage.linkedPropertyId) {
      setProperties(prev => prev.map(p =>
        p.id === newMortgage.linkedPropertyId ? { ...p, linkedMortgageId: newMortgage.id } : p
      ));
    }
    return newMortgage;
  };

  const updateMortgage = (mortgageId, updates) => {
    setMortgages(prev => {
      const oldMortgage = prev.find(m => m.id === mortgageId);
      // If linked property changed, update old and new property links
      if (oldMortgage && oldMortgage.linkedPropertyId !== updates.linkedPropertyId) {
        // Unlink old property
        if (oldMortgage.linkedPropertyId) {
          setProperties(prevP => prevP.map(p =>
            p.id === oldMortgage.linkedPropertyId ? { ...p, linkedMortgageId: null } : p
          ));
        }
        // Link new property
        if (updates.linkedPropertyId) {
          setProperties(prevP => prevP.map(p =>
            p.id === updates.linkedPropertyId ? { ...p, linkedMortgageId: mortgageId } : p
          ));
        }
      }
      return prev.map(m =>
        m.id === mortgageId ? { ...m, ...updates, updatedAt: getCurrentDate() } : m
      );
    });
  };

  const deleteMortgage = (mortgageId) => {
    setMortgages(prev => prev.filter(m => m.id !== mortgageId));
    // Unlink any properties that reference this mortgage
    setProperties(prev => prev.map(p =>
      p.linkedMortgageId === mortgageId ? { ...p, linkedMortgageId: null } : p
    ));
  };

  // Proposed transaction operations — proposals are computed in the component,
  // these functions just record the user's action and create the real transaction
  const confirmProposedTransaction = (proposal, overridePotAllocations = null) => {
    const txnData = {
      portfolioId: proposal.portfolioId,
      type: proposal.type,
      amount: proposal.amount,
      date: proposal.date,
      potAllocations: overridePotAllocations || proposal.potAllocations,
      description: proposal.description,
    };

    const realTransaction = addTransaction(txnData);
    if (!realTransaction) return null;

    setProposedTransactions(prev => [...prev, {
      ...proposal,
      status: 'confirmed',
      confirmedAt: getCurrentDate(),
      transactionId: realTransaction.id,
    }]);

    toast.success('Contribution confirmed');
    return realTransaction;
  };

  const rejectProposedTransaction = (proposal) => {
    setProposedTransactions(prev => [...prev, {
      ...proposal,
      status: 'rejected',
      rejectedAt: getCurrentDate(),
    }]);
    toast.success('Contribution skipped');
  };


  // School fee plan operations
  const addSchoolFeePlan = (plan) => {
    const newPlan = {
      ...plan,
      id: generateId('sfp'),
      createdAt: getCurrentDate(),
    };
    setSchoolFeePlans(prev => [...prev, newPlan]);
    toast.success('School fee plan added');
    return newPlan;
  };

  const updateSchoolFeePlan = (planId, updates) => {
    setSchoolFeePlans(prev =>
      prev.map(p => p.id === planId ? { ...p, ...updates } : p)
    );
  };

  const deleteSchoolFeePlan = (planId) => {
    setSchoolFeePlans(prev => prev.filter(p => p.id !== planId));
    toast.success('School fee plan deleted');
  };

  // Clear all data
  const clearAllData = async () => {
    try {
      await databaseService.clearAllData();
      setPortfolios([]);
      setGoals([]);
      setTransactions([]);
      setSnapshots([]);
      setProperties([]);
      setMortgages([]);
      setProposedTransactions([]);
      setSchoolFeePlans([]);
      setExchangeRates({
        baseCurrency: 'GBP',
        lastUpdated: new Date().toISOString().split('T')[0],
        rates: {
          GBP: 1.00,
          USD: 1.27,
        }
      });
      setSettings({ displayCurrency: 'GBP', theme: 'light' });
      toast.success('All data cleared successfully');
    } catch (error) {
      console.error('Error clearing data:', error);
      toast.error('Error clearing data');
    }
  };

  // Import all data
  const importAllData = (data) => {
    try {
      if (data.portfolios) setPortfolios(data.portfolios);
      if (data.goals) setGoals(data.goals);
      if (data.transactions) setTransactions(data.transactions);
      if (data.snapshots) setSnapshots(data.snapshots);
      if (data.fundPrices) setFundPrices(data.fundPrices);
      if (data.properties) setProperties(data.properties);
      if (data.mortgages) setMortgages(data.mortgages);
      if (data.proposedTransactions) setProposedTransactions(data.proposedTransactions);
      if (data.schoolFeePlans) setSchoolFeePlans(data.schoolFeePlans);
      if (data.exchangeRates) setExchangeRates(data.exchangeRates);
      if (data.settings) setSettings(data.settings);
      toast.success('Data imported successfully');
    } catch (error) {
      toast.error('Error importing data');
      console.error(error);
    }
  };

  const value = {
    // State
    portfolios,
    goals,
    transactions,
    snapshots,
    fundPrices,
    properties,
    mortgages,
    proposedTransactions,
    schoolFeePlans,
    exchangeRates,
    settings,
    loading,
    initialLoadComplete,

    // Portfolio operations
    addPortfolio,
    updatePortfolio,
    deletePortfolio,
    updatePortfolioValue,

    // Snapshot operations
    addSnapshot,

    // Goal operations
    addGoal,
    updateGoal,
    deleteGoal,
    reorderGoals,

    // Transaction operations
    addTransaction,
    updateTransaction,
    deleteTransaction,
    importTransactions,

    // Fund price operations
    addFundPrice,
    updateFundPrice,
    deleteFundPrice,

    // Property operations
    addProperty,
    updateProperty,
    deleteProperty,

    // Mortgage operations
    addMortgage,
    updateMortgage,
    deleteMortgage,

    // Proposed transaction operations
    confirmProposedTransaction,
    rejectProposedTransaction,

    // School fee plan operations
    addSchoolFeePlan,
    updateSchoolFeePlan,
    deleteSchoolFeePlan,

    // Exchange rate operations
    updateExchangeRates,
    refreshExchangeRates: async (forceRefresh = false) => {
      return await refreshExchangeRatesIfNeeded({
        exchangeRates,
        updateExchangeRates,
        silent: false, // Show toasts for manual refresh
        forceRefresh,
      });
    },

    // Settings operations
    updateSettings,

    // Data management
    clearAllData,
    importAllData,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
